/**
 * Drop zone — drag/drop, click para file picker, validação, a11y.
 */
import { t } from '@/i18n/index.js';
import { validateType, validateSize, fileToArrayBuffer } from '@/utils/files.js';
import { detectType } from '@/encoders/index.js';
import { showToast } from '@/ui/toast.js';

const ACCEPT_MIMES = ['image/png', 'image/jpeg', 'image/webp'];
const WARNING_THRESHOLD = 50;

/**
 * @typedef {Object} DropzoneCallbacks
 * @property {(files: Array<{file: File, type: string, buffer: ArrayBuffer}>) => void} onValidFiles
 */

/**
 * Monta o drop zone. Idempotente.
 * @param {HTMLElement} el
 * @param {DropzoneCallbacks} cb
 */
export function mountDropzone(el, cb) {
  if (!el) return;
  if (el.dataset.mounted === 'dropzone') return;
  el.dataset.mounted = 'dropzone';

  const input = createHiddenInput();
  el.appendChild(input);

  // click → abre picker
  el.addEventListener('click', (e) => {
    // Não abrir se clicou no warning text
    if (e.target.closest('.dropzone-warning')) return;
    input.click();
  });

  // keyboard: Enter / Space abre picker
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      input.click();
    }
  });

  input.addEventListener('change', (e) => {
    const files = [...(e.target.files || [])];
    handleFiles(files, cb);
    input.value = ''; // permite re-selecionar o mesmo arquivo
  });

  // drag and drop
  ['dragenter', 'dragover'].forEach((evt) => {
    el.addEventListener(evt, (e) => {
      e.preventDefault();
      e.stopPropagation();
      el.dataset.dragover = 'true';
    });
  });
  ['dragleave', 'drop'].forEach((evt) => {
    el.addEventListener(evt, (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (evt === 'dragleave' && e.target !== el) return;
      el.dataset.dragover = 'false';
    });
  });
  el.addEventListener('drop', (e) => {
    const files = [...(e.dataTransfer?.files || [])];
    handleFiles(files, cb);
  });

  // dragover no body inteiro previne o browser de abrir o arquivo
  document.addEventListener('dragover', (e) => e.preventDefault());
  document.addEventListener('drop', (e) => e.preventDefault());
}

function createHiddenInput() {
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.accept = ACCEPT_MIMES.join(',');
  input.style.display = 'none';
  input.setAttribute('aria-hidden', 'true');
  input.tabIndex = -1;
  return input;
}

/**
 * Processa um array de Files: valida cada um, separa válidos de inválidos,
 * chama onValidFiles com os válidos. Mostra toast pros inválidos.
 */
async function handleFiles(files, cb) {
  if (files.length === 0) return;

  // Warning se > 50
  const warningEl = document.querySelector('.dropzone-warning');
  if (warningEl) {
    if (files.length > WARNING_THRESHOLD) {
      warningEl.hidden = false;
      warningEl.textContent = t('dropzone.warning', { count: files.length });
    } else {
      warningEl.hidden = true;
    }
  }

  const valid = [];
  for (const file of files) {
    // 1. size check
    const sizeCheck = validateSize(file);
    if (!sizeCheck.ok) {
      showToast({ message: t('toast.tooLarge', { size: formatMb(file.size) }), type: 'error' });
      continue;
    }
    // 2. type check (MIME + magic bytes)
    const typeCheck = await validateType(file);
    if (!typeCheck.ok) {
      showToast({ message: typeCheck.error, type: 'error' });
      continue;
    }
    // 3. lê buffer pra detectar (validação extra) e passar pro worker
    try {
      const buffer = await fileToArrayBuffer(file);
      const detected = detectType(new Uint8Array(buffer));
      if (detected !== typeCheck.type) {
        showToast({ message: 'Tipo detectado não bate com extensão', type: 'error' });
        continue;
      }
      valid.push({ file, type: detected, buffer });
    } catch {
      showToast({ message: 'Falha ao ler arquivo', type: 'error' });
    }
  }

  if (valid.length > 0) {
    cb(valid);
  }
}

function formatMb(bytes) {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
